import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Box,
  Collapse,
  IconButton,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchClientCreditsWithQuotas, fetchCredit, fetchCreditPayoffBreakdown, fetchCreditQuotaStatements } from "./gestionCarteraApi";
import {
  formatDate,
  formatFullName,
  formatMoney,
  formatNumericDate,
  formatPercent,
  isQuotaOpen,
  isQuotaOverdue,
  quotaBalance,
  quotaCapital,
  quotaChargedTotal,
  quotaState,
  summarizeCredit,
  todayIso,
} from "./gestionCarteraFormat";
import { clientProfilePath, GESTION_CARTERA_PATH } from "./gestionCarteraRoutes";
import {
  Breadcrumbs,
  Card,
  cellSx,
  COLORS,
  headCellSx,
  labelSx,
  linkSx,
  PageContainer,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  StatusBadge,
} from "./gestionCarteraUi";
import ComponentesCuotaModal from "./modals/ComponentesCuotaModal";
import ReestructurarModal from "./modals/ReestructurarModal";
import CambiarDiaPagoModal from "./modals/CambiarDiaPagoModal";
import MasInfoCreditoModal from "./modals/MasInfoCreditoModal";
import PlanPagosOriginalModal from "./modals/PlanPagosOriginalModal";

const PLAN_COLUMNS = [
  { label: "N.º de cuota", width: "7%" },
  { label: "Vencimiento", width: "8.5%" },
  { label: "Valor de cuota", width: "9%" },
  { label: "Interés moratorio", width: "9%" },
  { label: "GAC", width: "6.5%" },
  { label: "Vr. cuota total", width: "9%" },
  { label: "Valor pagado", width: "9.5%", split: true },
  { label: "Fecha de pago", width: "8.5%" },
  { label: "Capital pagado", width: "9.5%" },
  { label: "Saldo capital", width: "9.5%" },
  { label: "Estado", width: "9%" },
  { label: "Acción", width: "5%" },
];
const SPLIT_CELL_SX = { borderLeft: "2px solid rgba(22, 30, 85, 0.28)" };
const planHeadSx = {
  ...headCellSx,
  backgroundColor: "#fff",
  whiteSpace: "normal",
  lineHeight: 1.2,
  fontSize: 12,
  px: 0.5,
  py: 1,
};
const planCellSx = {
  ...cellSx,
  fontSize: 13,
  whiteSpace: "nowrap",
  px: 0.5,
  py: 0.75,
};

/** El listado de cartera no trae GAC por cuota; el de condonaciones sí (quota.gac o pendingGac). */
const gacByQuotaFrom = (statements) => {
  const map = {};
  (statements || []).forEach((item) => {
    const quota = item?.quota || item;
    const id = quota?.uuid;
    if (!id) return;
    map[id] = quota.gac ?? quota.gacValue ?? item?.pending?.pendingGac;
  });
  return map;
};
const MAX_UNPAGED_QUOTAS = 24;
const QUOTAS_PER_PAGE = 12;
const PILL_BUTTON_SX = { minHeight: 35, px: 1.5, py: 0, fontSize: 14, fontWeight: 500, borderRadius: 999 };

/** Con más de 24 cuotas se pagina de 12 en 12, empezando en la página de la cuota vencida más antigua (o la próxima). */
const initialPlanPage = (quotas) => {
  const focusIndex = quotas.findIndex(isQuotaOpen);
  return focusIndex < 0 ? 0 : Math.floor(focusIndex / QUOTAS_PER_PAGE);
};

function DefinitionList({ items, columns = "repeat(auto-fit, minmax(170px, 1fr))" }) {
  return (
    <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: columns, gap: 2 }}>
      {items.map((item) => (
        <Box key={item.label} sx={item.sx}>
          <Typography component="dt" sx={labelSx}>{item.label}</Typography>
          <Typography component="dd" sx={{ m: "2px 0 0", fontWeight: 500, wordBreak: "break-word" }}>{item.value ?? "—"}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function KpiTile({ label, value, danger }) {
  return (
    <Card component="section" aria-label={label} sx={{ p: 1.5, minWidth: 0, borderColor: danger ? "#FDA29B" : COLORS.border }}>
      <Typography sx={labelSx}>{label}</Typography>
      <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 500, fontVariantNumeric: "tabular-nums", color: danger ? COLORS.danger : COLORS.primary }}>{value}</Typography>
    </Card>
  );
}

function OverdueBanner({ summary, quotasCount }) {
  const quota = summary.oldestOverdue;
  const moraText = quota.interesesMoratoriosGenerados > 0
    ? `Intereses de mora: ${formatMoney(quota.interesesMoratoriosGenerados)}.`
    : "Sin cargos de mora.";
  return (
    <Box role="alert" sx={{ display: "flex", gap: 1.5, p: "14px 18px", borderRadius: "12px", border: "1px solid #FDA29B", backgroundColor: "#FEF3F2", color: COLORS.danger }}>
      <ErrorOutlineIcon />
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          {`${summary.overdueCount} ${summary.overdueCount === 1 ? "cuota vencida" : "cuotas vencidas"} · ${summary.delayDays} días de atraso`}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
          {`Cuota ${String(quota.quotaNumber).padStart(2, "0")}/${quotasCount} · Vencimiento: ${formatDate(quota.payDate)} · Saldo: ${formatMoney(quotaBalance(quota))}. ${moraText}`}
        </Typography>
      </Box>
    </Box>
  );
}

function PaymentPlan({ credit, summary, today, gacByQuota, onViewComponents }) {
  const quotas = credit.quotas || [];
  const paginated = quotas.length > MAX_UNPAGED_QUOTAS;
  const [page, setPage] = useState(() => (paginated ? initialPlanPage(quotas) : 0));
  const [expanded, setExpanded] = useState(true);
  const visible = paginated ? quotas.slice(page * QUOTAS_PER_PAGE, (page + 1) * QUOTAS_PER_PAGE) : quotas;

  return (
    <Card component="section" aria-label="Comportamiento de pagos actual" sx={{ overflow: "hidden" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5, p: { xs: "12px", sm: "16px 20px" } }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.25 }}>
          <IconButton
            aria-label={expanded ? "Plegar comportamiento de pagos actual" : "Desplegar comportamiento de pagos actual"}
            aria-expanded={expanded}
            aria-controls="gc-comportamiento-pagos"
            onClick={() => setExpanded((current) => !current)}
            sx={{ color: COLORS.primary, transform: expanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}
          >
            <ExpandMoreIcon />
          </IconButton>
          <SectionTitle>Comportamiento de pagos actual</SectionTitle>
          <StatusBadge tone="green" label={`${summary.paidCount} pagadas`} />
          <StatusBadge tone="red" label={`${summary.overdueCount} vencidas`} />
          <StatusBadge tone="blue" label={`${summary.pendingCount} pendientes`} />
        </Box>
      </Box>
      <Collapse in={expanded} id="gc-comportamiento-pagos">
      <TableContainer sx={{ maxHeight: { xs: 480, xl: "calc(100vh - 240px)" }, overflowX: "auto" }}>
        <Table stickyHeader size="small" sx={{ width: "100%", minWidth: { xs: 1180, lg: 0 }, tableLayout: { xs: "auto", lg: "fixed" } }} aria-label="Comportamiento de pagos actual">
          <TableHead>
            <TableRow>
              {PLAN_COLUMNS.map((column) => (
                <TableCell key={column.label} sx={{ ...planHeadSx, width: { lg: column.width }, whiteSpace: { xs: "nowrap", lg: "normal" }, ...(column.split ? SPLIT_CELL_SX : null) }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((quota) => {
              const state = quotaState(quota, today);
              const overdue = isQuotaOverdue(quota, today);
              return (
                <TableRow key={quota.uuid} sx={{ backgroundColor: overdue ? "#FEF3F2" : undefined }}>
                  <TableCell sx={planCellSx}>{`${String(quota.quotaNumber).padStart(2, "0")}/${quotas.length}`}</TableCell>
                  <TableCell sx={planCellSx}>{formatNumericDate(quota.payDate)}</TableCell>
                  <TableCell sx={planCellSx}>{formatMoney(quota.value)}</TableCell>
                  <TableCell sx={planCellSx}>{formatMoney(quota.interesesMoratoriosGenerados)}</TableCell>
                  <TableCell sx={planCellSx}>{formatMoney(gacByQuota[quota.uuid])}</TableCell>
                  <TableCell sx={planCellSx}>{formatMoney(quotaChargedTotal(quota, gacByQuota[quota.uuid]))}</TableCell>
                  <TableCell sx={{ ...planCellSx, ...SPLIT_CELL_SX }}>{formatMoney(quota.paidValue)}</TableCell>
                  <TableCell sx={planCellSx}>{formatNumericDate(quota.paidDate)}</TableCell>
                  <TableCell sx={planCellSx}>{formatMoney(quotaCapital(quota))}</TableCell>
                  <TableCell sx={planCellSx}>{formatMoney(quota.saldoCapital)}</TableCell>
                  <TableCell sx={planCellSx}><StatusBadge tone={state.tone} label={state.label} /></TableCell>
                  <TableCell sx={planCellSx}>
                    <Tooltip title="Ver componentes">
                      <IconButton size="small" aria-label={`Ver componentes cuota ${quota.quotaNumber}`} onClick={() => onViewComponents(quota)} sx={{ color: COLORS.primary, p: 0.5 }}>
                        <ReceiptLongIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {paginated && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: "14px 20px" }}>
          <Pagination
            count={Math.ceil(quotas.length / QUOTAS_PER_PAGE)}
            page={page + 1}
            onChange={(_, newPage) => setPage(newPage - 1)}
            shape="rounded"
          />
        </Box>
      )}
      </Collapse>
    </Card>
  );
}

/** Pantalla 3 · Detalle del crédito. */
export default function GestionCarteraCredito() {
  const { clientUuid, creditUuid } = useParams();
  const today = todayIso();
  const [state, setState] = useState({ status: "loading", credits: [], creditDto: null, payoff: null, gacByQuota: {} });
  const [modal, setModal] = useState({ name: null, quotaUuid: null });

  const load = useCallback(async () => {
    setState((current) => ({ ...current, status: "loading" }));
    try {
      const [credits, creditDto, payoff, statements] = await Promise.all([
        fetchClientCreditsWithQuotas(clientUuid),
        fetchCredit(creditUuid),
        fetchCreditPayoffBreakdown(creditUuid).catch(() => null),
        fetchCreditQuotaStatements(creditUuid).catch(() => []),
      ]);
      setState({ status: "ready", credits, creditDto, payoff, gacByQuota: gacByQuotaFrom(statements) });
    } catch (error) {
      setState({ status: "error", credits: [], creditDto: null, payoff: null, gacByQuota: {} });
    }
  }, [clientUuid, creditUuid]);

  useEffect(() => {
    load();
  }, [load]);

  const credit = useMemo(() => state.credits.find((item) => item.uuid === creditUuid), [state.credits, creditUuid]);
  const activeCreditsCount = state.credits.filter((item) => summarizeCredit(item, today).active).length;
  const closeModal = () => setModal({ name: null, quotaUuid: null });
  const afterAction = () => {
    closeModal();
    load();
  };

  if (state.status === "loading") {
    return (
      <PageContainer>
        <Skeleton variant="rounded" height={40} />
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={420} />
      </PageContainer>
    );
  }

  if (state.status === "error" || !credit) {
    return (
      <PageContainer>
        <Breadcrumbs items={[{ label: "Clientes", to: GESTION_CARTERA_PATH }, { label: "Cliente", to: clientProfilePath(clientUuid) }, { label: "Crédito" }]} />
        <Card role="alert" sx={{ p: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: COLORS.primary }}>No pudimos cargar el crédito</Typography>
          <SecondaryButton sx={{ mt: 2 }} onClick={load}>Reintentar</SecondaryButton>
        </Card>
      </PageContainer>
    );
  }

  const summary = summarizeCredit(credit, today);
  // Saldo vivo del crédito a hoy calculado por backend (capital + intereses + mora + GAC + FGA + seguros).
  const totalBalance = state.creditDto?.balanceCredit ?? credit.balanceCredit ?? null;
  const client = credit.client || {};
  const clientName = formatFullName(client);
  const storeName = credit.commerce?.companyName || credit.commerce?.name || "";
  const storeId = credit.commerce?.oldId;
  const storeIdLabel = storeId == null || storeId === "" ? "" : `ID${storeId}`;
  const subtitle = [storeName, storeIdLabel].filter(Boolean).join(" - ");

  return (
    <PageContainer>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 1.5 }}>
        <Breadcrumbs items={[{ label: "Clientes", to: GESTION_CARTERA_PATH }, { label: clientName, to: clientProfilePath(clientUuid) }, { label: `Crédito ${credit.oldId}` }]} />
        <Box component={RouterLink} to={clientProfilePath(clientUuid)} sx={{ ...linkSx, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          <ArrowBackIcon fontSize="small" /> Volver al cliente
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography component="h1" sx={{ fontSize: 14, fontWeight: 700, color: COLORS.primary }}>{`Crédito ${credit.oldId}`}</Typography>
            <StatusBadge tone={summary.state.tone} label={summary.state.label} />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, width: { xs: "100%", sm: "auto" } }}>
          <PrimaryButton onClick={() => setModal({ name: "reestructurar" })} sx={{ ...PILL_BUTTON_SX, width: { xs: "100%", sm: "auto" } }}>Reestructurar</PrimaryButton>
          <PrimaryButton onClick={() => setModal({ name: "diaPago" })} sx={{ ...PILL_BUTTON_SX, width: { xs: "100%", sm: "auto" } }}>Cambiar día de pago</PrimaryButton>
        </Box>
      </Box>

      {summary.inMora && <OverdueBanner summary={summary} quotasCount={summary.quotasCount} />}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, minmax(0, 1fr))", lg: "repeat(6, minmax(0, 1fr))" }, gap: 1.5 }}>
        <KpiTile label="Valor crédito" value={formatMoney(credit.totalValue)} />
        <KpiTile label="Saldo capital" value={formatMoney(credit.saldoCapital)} />
        <KpiTile label="Saldo total (Saldo insoluto)" value={formatMoney(totalBalance)} />
        <KpiTile label="Saldo vencido" value={formatMoney(summary.overdueValue)} danger={summary.overdueValue > 0} />
        <KpiTile label="Mora pendiente" value={formatMoney(state.payoff?.mora)} />
        <KpiTile label="GAC pendiente" value={formatMoney(state.payoff?.gac)} />
      </Box>

      <Card component="section" aria-label="Condiciones del crédito" sx={{ p: { xs: 2, sm: 3 }, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <SectionTitle>Condiciones del crédito</SectionTitle>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="Ver plan de pagos original">
                <IconButton aria-label="Ver plan de pagos original" onClick={() => setModal({ name: "planOriginal" })} sx={{ color: COLORS.primary }}>
                  <ListAltIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Más información del crédito">
                <IconButton aria-label="Más información del crédito" onClick={() => setModal({ name: "masInfo" })} sx={{ color: COLORS.primary }}>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <DefinitionList
            columns={{ xs: "1fr", sm: "1fr 1fr", lg: "minmax(190px, 2fr) 88px minmax(120px, 1fr) minmax(150px, 1.2fr)" }}
            items={[
              { label: "Comercio aliado", value: subtitle || "—" },
              { label: "Plazo", value: `${credit.quotasNumber ?? summary.quotasCount} cuotas` },
              { label: "Valor de cuota", value: formatMoney(credit.quotaValue), sx: { pl: { lg: 4 } } },
              { label: "Tasa de interés (Mes vencido)", value: formatPercent(credit.interestRate) },
            ]}
          />
        </Card>

      <PaymentPlan credit={credit} summary={summary} today={today} gacByQuota={state.gacByQuota} onViewComponents={(quota) => setModal({ name: "componentes", quota })} />

      <ComponentesCuotaModal
        open={modal.name === "componentes"}
        quota={modal.quota}
        quotasCount={summary.quotasCount}
        creditUuid={credit.uuid}
        clientUuid={clientUuid}
        onClose={closeModal}
      />
      <PlanPagosOriginalModal
        open={modal.name === "planOriginal"}
        credit={state.creditDto}
        title={`Crédito ${credit.oldId}`}
        onClose={closeModal}
      />
      <MasInfoCreditoModal
        open={modal.name === "masInfo"}
        credit={state.creditDto}
        title={`Crédito ${credit.oldId}`}
        onClose={closeModal}
      />
      <ReestructurarModal
        open={modal.name === "reestructurar"}
        credit={credit}
        balanceCredit={totalBalance}
        clientCredits={state.credits}
        onClose={closeModal}
        onDone={afterAction}
      />
      <CambiarDiaPagoModal
        open={modal.name === "diaPago"}
        credit={credit}
        clientUuid={clientUuid}
        activeCreditsCount={activeCreditsCount}
        onClose={closeModal}
        onDone={afterAction}
      />
    </PageContainer>
  );
}
